import type { PlanInfo } from "schemas/billing";
import type { BillingPlanChangeSurveyModalProps } from "../AppModalTypes";

import * as React from "react";

import { useCurrentWorkspace } from "@editor/contexts/WorkspaceDashboardContext";
import useInitializeSubscriptionUpdateMutation from "@editor/hooks/trpc/useInitializeSubscriptionUpdateMutation";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useModal } from "@editor/hooks/useModal";
import { analytics } from "@editor/infra/analytics";
import { useGetModalDataQuery } from "@editor/reducers/sanity-reducer";
import { trpc } from "@editor/utils/trpc";

import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import SelectableButtons from "@replo/design-system/components/selectable-buttons/SelectableButtons";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { skipToken } from "@tanstack/react-query";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { filterNulls } from "replo-utils/lib/array";
import {
  BILLING_PLAN_CONFIRMATION_PARAM,
  getBillingPlanInfoByTier,
} from "schemas/billing";

type WorkspaceInfoProps = {
  planInfo: PlanInfo | undefined;
  workspaceName: string | undefined;
  projectsCount: number;
  pagesCount: number;
};

const WorkspaceInfo: React.FC<WorkspaceInfoProps> = ({
  planInfo,
  workspaceName,
  projectsCount,
  pagesCount,
}) => (
  <InlineAlert variant="error">
    <p className="text-muted text-base">
      You're cancelling your{" "}
      <span className="text-default font-semibold">
        Replo {planInfo?.displayName}
      </span>{" "}
      plan for{" "}
      <span className="text-default font-semibold">{workspaceName}</span>, which
      has{" "}
      <span className="text-default font-semibold">
        {projectsCount} projects
      </span>{" "}
      and{" "}
      <span className="text-default font-semibold">
        {pagesCount} published pages
      </span>
      .
    </p>
  </InlineAlert>
);

export const BillingPlanChangeSurveyModal: React.FC<
  BillingPlanChangeSurveyModalProps
> = ({ tier, downgradeTo, newBillingInterval, source, workspaceId }) => {
  const modal = useModal();
  const { data: modalData, isLoading: isLoadingModalData } =
    useGetModalDataQuery({});
  const [selectedButtons, setSelectedButtons] = React.useState<string[]>([]);
  const { user } = useCurrentUser();

  const [textFeedback, setTextFeedback] = React.useState("");

  const logAnalyticsData = () => {
    analytics.logEvent("billing.downgrade.survey", {
      previousPlan: tier,
      currentPlan: downgradeTo,
      reasons: filterNulls(selectedButtons),
      feedback: textFeedback,
    });
  };

  const hasFilledOutSurvey = selectedButtons.length > 0;
  const [hasEverTriedToSubmit, setHasEverTriedToSubmit] = React.useState(false);

  const [, setSearchParams] = useSearchParams();

  const { initializeSubscriptionUpdate } =
    useInitializeSubscriptionUpdateMutation();

  // Note (Evan, 2024-04-24): Handle loading state ourselves since we still want to show a
  // loading state if the mutation has succeeded and we are waiting for a redirect.
  const [isLoading, setIsLoading] = React.useState(false);

  const [currentStep, setCurrentStep] = React.useState<"reasons" | "feedback">(
    "reasons",
  );

  const workspace = useCurrentWorkspace();

  const { data: responseData } =
    trpc.workspace.getPublishedElementsCountByWorkspaceId.useQuery(
      workspaceId ?? skipToken,
    );

  const { page: pagesCount } = {
    page: responseData?.page ?? 0,
  };

  const { data: projects } = trpc.project.listWithStats.useQuery(
    workspaceId ? { workspaceId } : skipToken,
  );
  const workspaceProjectLength = projects?.projects.length ?? 0;
  const workspaceName = workspace?.name;

  const planInfo = getBillingPlanInfoByTier(tier);

  const { data: activeSubscription } =
    trpc.subscriptions.getActiveSubscriptionByWorkspace.useQuery(
      workspaceId ?? skipToken,
    );

  const { data: nextBillingDate } = trpc.billing.getNextBillingDate.useQuery(
    workspaceId && activeSubscription?.id
      ? {
          workspaceId,
          subscriptionId: activeSubscription.id,
        }
      : skipToken,
  );

  const selectedButtonData = React.useMemo(() => {
    if (!modalData || selectedButtons.length === 0) {
      return null;
    }
    return modalData.find((item) => item.buttonText === selectedButtons[0]);
  }, [modalData, selectedButtons]);

  const handleSubmit = async () => {
    logAnalyticsData();
    setIsLoading(true);
    try {
      const result = await initializeSubscriptionUpdate({
        workspaceId,
        tier: downgradeTo,
        billingInterval: newBillingInterval,
        source,
      });

      if (!result.success) {
        setIsLoading(false);
      }
      // Note (Evan, 2024-04-24): The case where confirmationRequired is true
      // is handled in the RTK API slice
      else if (!result.confirmationRequired) {
        modal.closeModal({ type: "billingPlanChangeSurvey" });
      }
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const getButtonText = () => {
    if (currentStep === "feedback") {
      return "Finish";
    }
    return "Cancel Plan";
  };

  const reasonsStepTitle = user?.firstName
    ? `${user.firstName}, before you go...`
    : "Before You Go...";

  const formattedEndDate = React.useMemo(() => {
    if (nextBillingDate) {
      try {
        return format(new Date(nextBillingDate), "MMMM do, yyyy");
      } catch (error) {
        console.error("Failed to format next billing date:", error);
        return "the end of your current billing period";
      }
    }
    return "the end of your current billing period";
  }, [nextBillingDate]);

  return (
    <Modal
      isOpen={true}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          setSearchParams(
            (params) => {
              if (!params.has(BILLING_PLAN_CONFIRMATION_PARAM)) {
                return params;
              }
              params.delete(BILLING_PLAN_CONFIRMATION_PARAM);
              return new URLSearchParams(params);
            },
            { replace: true },
          );
          modal.closeModal({ type: "billingPlanChangeSurvey" });
        }
      }}
      title={
        currentStep === "reasons"
          ? reasonsStepTitle
          : "We're sorry to see you go! 😢"
      }
      size="base"
      data-testid="billing-plan-change-survey"
      footer={
        <div className="flex flex-row justify-end gap-2">
          {currentStep === "reasons" && (
            <Button
              variant="secondary"
              size="base"
              onClick={() => {
                if (!isLoading) {
                  modal.closeModal({ type: "billingPlanChangeSurvey" });
                }
              }}
            >
              Nevermind
            </Button>
          )}
          <Button
            variant={currentStep === "reasons" ? "danger" : "primary"}
            size="base"
            onClick={() => {
              if (isLoading) {
                return;
              }
              if (currentStep === "reasons") {
                setHasEverTriedToSubmit(true);
                if (hasFilledOutSurvey) {
                  setCurrentStep("feedback");
                }
              } else {
                void handleSubmit();
              }
            }}
            isLoading={isLoading}
            disabled={isLoading}
          >
            {getButtonText()}
          </Button>
        </div>
      }
    >
      {currentStep === "reasons" ? (
        <div className="flex flex-col">
          <p className="text-base">
            Could you please share your top reason why Replo isn't a good fit?
          </p>
          {hasEverTriedToSubmit && !selectedButtons.length && (
            <p className="text-danger mb-1 mt-2 text-base">
              * Please select at least one reason
            </p>
          )}
          {isLoadingModalData ? (
            <Skeleton className="my-2 h-28" />
          ) : (
            <SelectableButtons
              multiSelect={false}
              options={[...(modalData ?? [])]
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((item) => ({
                  label: item.buttonText,
                  value: item.buttonText,
                }))}
              value={selectedButtons}
              onChange={setSelectedButtons}
              layoutClassName="my-4"
            />
          )}
          <WorkspaceInfo
            planInfo={planInfo}
            workspaceName={workspaceName}
            projectsCount={workspaceProjectLength}
            pagesCount={pagesCount}
          />
        </div>
      ) : (
        <div className="flex w-full flex-col gap-1">
          <p className="mb-2 text-base font-semibold">
            {selectedButtonData?.customQuestion ??
              "Anything you want to share with the team?"}
          </p>
          <Textarea
            size="base"
            layoutClassName="h-24 w-full mb-3"
            value={textFeedback}
            onChange={setTextFeedback}
            placeholder="Anything you share will be helpful for our small team."
            autoFocus
          />
          <InlineAlert variant="info">
            You'll keep access to your Replo {planInfo?.displayName} plan until{" "}
            {formattedEndDate}.
          </InlineAlert>
        </div>
      )}
    </Modal>
  );
};
